import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaCheckCircle, FaUsers } from 'react-icons/fa';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import AnalyticsCharts from '../../../../components/AnalyticsCharts.jsx';
import PieChartCard from '../../../../components/PieChartCard.jsx';
import CollapsibleRecentEvents from '../../../../components/CollapsibleRecentEvents.jsx';
import { fetchOrganiserOverviewMetrics } from '../../../../utils/api/organiserApi.js';
import '../css/EventsOverview.css';

function OrganiserEventsOverview() {
  const nav = useNavigate();
  const [metrics, setMetrics] = useState({
    totalEvents: 0,
    completedEvents: 0,
    activeEvents: 0,
    averageAttendance: 0,
    recentEvents: [],
    eventStatusBreakdown: [],
    monthlyPerformance: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        console.log('[EventsOverview] 📅 Fetching — API: fetchOrganiserOverviewMetrics()');
        const data = await fetchOrganiserOverviewMetrics();
        console.log('[EventsOverview] ✅ Metrics loaded:', {
          columns: 'totalEvents, completedEvents, activeEvents, averageAttendance, recentEvents[{event_id,event_name,...}], eventStatusBreakdown[{status,count,percent}], monthlyPerformance[{month,events,avgAttendance}]',
          formula: 'averageAttendance = (totalAttended / totalCapacity) * 100  [capacity utilisation %]',
          data,
        });
        setMetrics((prev) => ({
          ...prev,
          ...data,
          recentEvents: Array.isArray(data?.recentEvents) ? data.recentEvents : [],
          eventStatusBreakdown: Array.isArray(data?.eventStatusBreakdown) ? data.eventStatusBreakdown : [],
          monthlyPerformance: Array.isArray(data?.monthlyPerformance) ? data.monthlyPerformance : [],
        }));
        setError(null);
      } catch (err) {
        console.error('[EventsOverview] ❌ Failed to load metrics:', err);
        setError(err.message || 'Failed to load organiser metrics');
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, []);

  const overviewCards = [
    {
      icon: <FaCalendarAlt />,
      title: 'Total Events',
      value: metrics.totalEvents,
      subtitle: 'All events created',
      color: '#EFB034FF'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Completed',
      value: metrics.completedEvents,
      subtitle: 'Successfully hosted',
      color: '#EFB034FF'
    },
    {
      icon: <FaClock />,
      title: 'Active',
      value: metrics.activeEvents,
      subtitle: 'Currently running',
      color: '#EFB034FF'
    },
    {
      icon: <FaUsers />,
      title: 'Avg Attendance',
      value: `${metrics.averageAttendance ?? 0}%`,
      subtitle: 'Capacity utilization',
      color: '#EFB034FF'
    }
  ];

  const charts = [
    {
      type: 'line',
      data: (metrics.monthlyPerformance ?? []).map((item) => ({
        month: item.month,
        events: item.events,
        attendance: item.avgAttendance,
      })),
      title: 'Monthly Event Trends',
      xAxisKey: 'month',
      lines: [
        { dataKey: 'events', color: '#EFB034FF', name: 'Total Events' },
        { dataKey: 'attendance', color: '#c3e1f8', name: 'Avg Attendance %' }
      ],
      height: 210
    }
  ];

  const pieChartData = (metrics.eventStatusBreakdown ?? []).map((item) => ({
    name: item.status,
    value: item.count,
    percentage: item.percent,
  }));
  const pieChartColors = ['#EFB034FF', '#c3e1f8', '#b8f5cd'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleLogout = () => {
    logout(nav);
  };

  if (loading) {
    return (
      <div className="events-overview">
        <GlobalHeader
          title="My Events Overview"
          subtitle="Loading organiser metrics..."
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-overview">
        <GlobalHeader
          title="My Events Overview"
          subtitle="Unable to load organiser metrics"
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-overview">
      <GlobalHeader
        title="My Events Overview"
        subtitle="Track your event performance and status"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="events-overview__main">
        <OverviewCards cards={overviewCards} />
        <CollapsibleRecentEvents events={metrics.recentEvents} formatCurrency={formatCurrency} />
        <section className="analytics-charts">
          <div className="monthly-trends-card">
            <AnalyticsCharts charts={charts} />
          </div>
          <PieChartCard
            data={pieChartData}
            title="Event Status Distribution"
            dataKey="value"
            nameKey="name"
            colors={pieChartColors}
          />
        </section>
      </main>
    </div>
  );
}

export default OrganiserEventsOverview;
